import { useState, useEffect, useRef, useCallback } from 'react';

export default function ClientsSection() {
  const [metrics, setMetrics] = useState({
    customerSatisfaction: '4.8/5',
    satisfactionPercentage: 96
  });
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);

  const fetchMetrics = useCallback(async () => {
    try {
      // Fetch real metrics from Freshservice via Supabase Edge Function
      const response = await fetch('https://txyawdaylusoolspukgs.supabase.co/functions/v1/freshservice-metrics');
      if (response.ok) {
        const data = await response.json();
        // Use requestAnimationFrame to batch state updates and avoid forced reflow
        requestAnimationFrame(() => {
          setMetrics({
            customerSatisfaction: data.customerSatisfaction || '4.8/5',
            satisfactionPercentage: data.satisfactionPercentage || 96
          });
        });
      }
    } catch (error) {
      console.log('Using default metrics - Freshservice connection issue');
    }
  }, []);

  useEffect(() => {
    fetchMetrics();
    // Refresh metrics every 30 minutes (increased cache lifetime)
    const interval = setInterval(fetchMetrics, 30 * 60 * 1000);
    return () => clearInterval(interval);
  }, [fetchMetrics]);

  // Intersection observer for animations
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          // Use requestAnimationFrame to avoid forced reflow during animation trigger
          requestAnimationFrame(() => {
            setIsVisible(true);
          });
          observer.disconnect();
        }
      },
      { threshold: 0.1, rootMargin: '50px' }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const clients = [
    {
      name: 'Richmond Metropolitan Habitat for Humanity',
      location: 'Richmond, VA',
      logo: 'https://static.readdy.ai/image/bbcc86dc4c3a385b7f9b9f3a7e24f053/94c9c235fd25fa6dc8da3fc0ae04425b.png',
      website: 'https://www.richmondhabitat.org'
    },
    {
      name: 'Hanover King William Habitat for Humanity',
      location: 'Hanover County, VA',
      logo: 'https://static.readdy.ai/image/bbcc86dc4c3a385b7f9b9f3a7e24f053/1a0480f31867d572431f1048573ab45a.png',
      website: 'https://www.hkwhabitat.org'
    },
    {
      name: 'Piedmont Habitat for Humanity',
      location: 'Farmville, VA',
      logo: 'https://static.readdy.ai/image/bbcc86dc4c3a385b7f9b9f3a7e24f053/ab92b8e37dee0e97ec4edb1006c3e2d2.png',
      website: 'https://www.piedmonthabitat.org'
    },
    {
      name: 'Children Incorporated',
      location: 'Richmond, VA',
      logo: 'https://static.readdy.ai/image/bbcc86dc4c3a385b7f9b9f3a7e24f053/3815c0c81113fddae5d8b67b94a4fac2.png',
      website: 'https://www.childrenincorporated.org'
    },
    {
      name: 'Sportable',
      location: 'Richmond, VA',
      logo: 'https://static.readdy.ai/image/bbcc86dc4c3a385b7f9b9f3a7e24f053/f61022f25e7af4d234c5f49f59c554be.png',
      website: 'https://www.sportable.org'
    },
    {
      name: 'The Arc of Virginia',
      location: 'Richmond, VA',
      logo: 'https://static.readdy.ai/image/bbcc86dc4c3a385b7f9b9f3a7e24f053/9ba7a81c290dce457912a040933caa48.webp',
      website: 'https://www.thearcofva.org'
    },
    {
      name: 'Community Brain Injury Services',
      location: 'Richmond, VA',
      logo: 'https://static.readdy.ai/image/bbcc86dc4c3a385b7f9b9f3a7e24f053/ed18124ed7c3a0da409b7fa5cf71ea4a.webp',
      website: 'https://www.communitybraininjury.org'
    },
    {
      name: 'Garden Club of Virginia',
      location: 'Richmond, VA',
      logo: 'https://static.readdy.ai/image/bbcc86dc4c3a385b7f9b9f3a7e24f053/2d0260af2e4d11514bfc4d097a5ae270.jfif',
      website: 'https://www.gcvirginia.org'
    }
  ];

  return (
    <section ref={sectionRef} id="clients" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">
            Trusted by Leading Virginia Nonprofits
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            We're proud to partner with diverse nonprofit organizations across Virginia, 
            helping them leverage technology to amplify their impact and serve their communities better. 
            Here are a few of the organizations we work with:
          </p>
        </div>

        {/* Client cards grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
          {clients.map((client, index) => (
            <div 
              key={index} 
              className="bg-gray-50 rounded-xl p-6 hover:shadow-lg transition-shadow duration-300 text-center flex flex-col justify-between min-h-[240px] md:h-[280px]"
            >
              <div className="mb-4">
                <div className="w-full h-16 flex items-center justify-center mb-4 flex-shrink-0">
                  <img 
                    src={client.logo}
                    alt={`${client.name} logo`}
                    className="max-w-full max-h-full object-contain"
                    loading="lazy"
                    decoding="async"
                    width="120"
                    height="64"
                  />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2 md:h-[56px] flex items-center justify-center leading-tight">{client.name}</h3>
                <p className="text-sm text-blue-600 mb-3 font-medium">{client.location}</p>
              </div>
              {client.website ? (
                <a 
                  href={client.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-blue-600 hover:text-blue-800 underline cursor-pointer whitespace-nowrap transition-colors duration-200 flex-shrink-0"
                >
                  Visit Website
                </a>
              ) : (
                <p className="text-sm text-gray-600 leading-relaxed flex-shrink-0">{client.description}</p>
              )}
            </div>
          ))}
        </div>

        {/* Join our community section with proper spacing */}
        <div className="bg-blue-50 rounded-2xl p-6 md:p-12 text-center mb-12">
          <h3 className="text-2xl font-bold text-gray-900 mb-4">
            Join Our Growing Community of Satisfied Clients
          </h3>
          <p className="text-lg text-gray-600 mb-6 max-w-2xl mx-auto">
            From small local charities to large statewide organizations, we provide scalable 
            IT solutions that grow with your nonprofit's needs and budget.
          </p>
          {/* Metrics grid with proper mobile spacing */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-center">
            <div className="flex flex-col justify-center py-4">
              <div className="text-3xl font-bold text-blue-600 mb-2">99.9%</div>
              <div className="text-gray-700">Uptime Achieved</div>
            </div>
            <div className="flex flex-col justify-center py-4">
              <div className="text-3xl font-bold text-blue-600 mb-2">100%</div>
              <div className="text-gray-700">Nonprofit Focused</div>
            </div>
            <div className="flex flex-col justify-center py-4">
              <div className="text-3xl font-bold text-blue-600 mb-2">98%</div>
              <div className="text-gray-700">Client Retention Rate</div>
            </div>
          </div>
        </div>

        {/* Customer Satisfaction Metrics with proper mobile spacing */}
        <div className="bg-white rounded-xl shadow-lg p-6 md:p-8 mt-12">
          <div className="flex flex-col md:flex-row items-center justify-center mb-6">
            <div className="w-12 h-12 bg-yellow-500 rounded-lg flex items-center justify-center mb-4 md:mb-0 md:mr-4 flex-shrink-0">
              <i className="ri-star-fill text-xl text-white w-6 h-6 flex items-center justify-center"></i>
            </div>
            <div className="text-center">
              <h3 className="text-2xl font-bold text-gray-900">Customer Satisfaction</h3>
              <p className="text-gray-600">Real-time feedback from our clients</p>
            </div>
          </div>
          
          {/* Metrics grid with proper mobile spacing */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-md mx-auto">
            <div className="text-center flex flex-col justify-center py-6">
              <div className="text-4xl font-bold text-yellow-500 mb-2">{metrics.customerSatisfaction}</div>
              <div className="text-gray-600 mb-4">Average Rating</div>
              <div className="flex justify-center space-x-1">
                {[1, 2, 3, 4, 5].map((star) => (
                  <i 
                    key={star} 
                    className={`ri-star-fill text-xl w-6 h-6 flex items-center justify-center ${
                      star <= Math.floor(parseFloat(metrics.customerSatisfaction)) 
                        ? 'text-yellow-400' 
                        : 'text-gray-300'
                    }`}
                  ></i>
                ))}
              </div>
            </div>
            
            <div className="text-center flex flex-col justify-center py-6">
              <div className="text-4xl font-bold text-green-500 mb-2">{metrics.satisfactionPercentage}%</div>
              <div className="text-gray-600 mb-4">Satisfied Clients</div>
              <div className="w-full bg-gray-200 rounded-full h-3">
                <div 
                  className="bg-green-500 h-3 rounded-full transition-all duration-500 ease-out"
                  style={{ 
                    width: `${isVisible ? metrics.satisfactionPercentage : 0}%`,
                    transform: 'translateZ(0)',
                    willChange: 'width'
                  }}
                ></div>
              </div>
              <div className="text-xs text-gray-500 mt-2">4+ star ratings</div>
            </div>
          </div>
        </div>

      </div>
    </section>
  );
}
